import React, { useState, useEffect } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore } from '../../../store'

import { amenitySchema } from '../../../schemas/visitorInfo'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Fieldset, FieldSection } from 'components/ui/fieldset'
import { FooterEditEntity, FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { Input } from 'components/ui/input'
import { Description, Image, Title } from 'components/ui/list-item-label'

import { LayoutFullWidthPage } from 'layouts/full-width-page'

import { Amenity } from 'generated/mos/org/getty/visitorinfo'
import { Language } from 'generated/mos/i18n'
import { Media } from 'generated/mos/media'
import { GetAmenityRequest } from 'generated/mos/org/getty/visitorinfoaccess'
import { UpdateAmenityRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { bffEndpoint } from 'helpers/bff'
import { pick, areObjectsEqual } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { translateValues, TranslationFormBags, convertEntityToFormValues } from 'helpers/schema'
import { isEnglish, parseLanguage } from 'helpers/i18n'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getAmenitySubnav } from '../subnav'

import { LANGUAGES, LanguageCode } from '../../../constants'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetAmenityRequest' | 'gettyUpdateAmenityRequest'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyStore['getty'], 'amenityUpdate'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly refId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps

type FormValues = {
  name: string | undefined;
  description: string | undefined;
  imageAltText: string | undefined;
  creditText: string | undefined;
}

type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
}

const HEADER_HEIGHT = 100

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT)}px);
`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const AmenityItem = styled.div`
  display: flex;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  padding-top: ${space(8)};
  padding-bottom: ${space(6)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL60};
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag } = props
  const isDisabled = isEnglish(language)
  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          <Input
            label="Title"
            field="name"
            isDisabled={isDisabled}
          />
          <FieldSection />
          <Input
            label="Image Alternate Text"
            description="Description of what the image contains if it cannot be shown."
            field="headerImageAltText"
            isDisabled={isDisabled}
          />
          <FieldSection />
          <Input
            component="textarea"
            label="Description"
            field="descriptionText"
            isDisabled={isDisabled}
          />
          <Input
            component="textarea"
            label="Extended Description"
            field="extendedDescriptionText"
            isDisabled={isDisabled}
          />
          <Input
            component="textarea"
            label="Opening hours"
            field="openingHoursText"
            isDisabled={isDisabled}
          />
          <Input
            label="Location text"
            field="locationText"
            isDisabled={isDisabled}
          />
          <FieldSection />
        </Fieldset>

      </FormData>
    </Panel>
  ) : null
}

const AmenityTranslationsView = (props: Props) => {
  const {
    gettyGetAmenityRequest,
    gettyUpdateAmenityRequest,
    historyPush,
    projectId,
    refId,
    amenityUpdate,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined)
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined)

  const backLocation = `/project/${projectId}/visitor-info`

  const data = statusSelector.data(amenityUpdate)
  const amenity = data && data.amenity

  const hostName = bffEndpoint(window.location.hostname)

  // load initial data
  useEffect(() => {
    if (amenityUpdate.status === Status.Idle || amenity && Amenity.mustRef(amenity.ref).id !== refId) {
      gettyGetAmenityRequest({
        type: GetAmenityRequest.refName,
        ref: {
          typename: Amenity.refName,
          id: refId
        }
      })
    }
  })

  useEffect(() => {
    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {
          const amenityValues: Partial<Amenity.Entity> = pick({
            ...Amenity.defaults,
            ...amenity
          }, 'name', 'headerImageAltText', 'descriptionText', 'extendedDescriptionText', 'openingHoursText', 'locationText')
          const initialFormValues: FormValues | undefined = amenity ?
            convertEntityToFormValues<Amenity.Entity>(amenityValues, amenitySchema, undefined, curr) as FormValues :
            undefined
          return {
            ...acc,
            [curr.languageCode]: createFormBag(initialFormValues)
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }
    if (amenity && amenityUpdate.status === Status.Ready) {
      const initValues = getInitialValues()
      setInitialValues(initValues)
      setFormBags(initValues)
    }

  }, [amenity, amenityUpdate])

  const modified = initialValues && formBags && !areObjectsEqual(initialValues, formBags)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm)
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, amenity: Amenity.Entity) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false;

    // Assemble Payload
    const translatedValues = translateValues<Amenity.Entity, FormValues, LanguageCode>(amenitySchema, amenity, LANGUAGES, formBags)
    // Update Amenity with changes.
    gettyUpdateAmenityRequest({
      ...UpdateAmenityRequest.defaults,
      ref: Amenity.mustRef(amenity.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['name', 'headerImageAltText', 'openingHoursText', 'descriptionText', 'extendedDescriptionText', 'locationText']
      },
      name: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('name')
      },
      headerImageAltText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('headerImageAltText')
      },
      openingHoursText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('openingHoursText')
      },
      descriptionText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('descriptionText')
      },
      extendedDescriptionText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('extendedDescriptionText')
      },
      locationText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('locationText')
      },
    })

    return false
  }

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if(formBags) {
      setFormBags({
        ...formBags,
        [languageCode]: bag
      })
    }
  }
  const title = amenity && amenity.name && amenitySchema.name ? amenitySchema.name.read(amenity.name) as string : ''

  const amenityImage = amenity && amenity.headerImageRef && amenitySchema.headerImageRef.read(amenity.headerImageRef) as Media.Ref
  const amenityImageId = amenityImage && amenityImage.id

  const imagePath = amenityImageId && hostName ? `${hostName}/images/${amenityImageId}.jpg?s=crop&h=92&w=92` : ''

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate "${title}"`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getAmenitySubnav(projectId, refId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title={title}
        content={() => (
          <StatusGuard status={amenityUpdate} mode="form">
            <Scroll>
              <Panels>
                {formBags && LANGUAGES.map((value) => {
                  const languageCode: LanguageCode = value.languageCode as LanguageCode
                  return (
                    <TranslationForm key={languageCode} language={value} formBag={formBags[languageCode]} setFormBag={updateFormBags(languageCode)} />
                  )
                })}
              </Panels>
            </Scroll>
          </StatusGuard>
        )}
        footer={() => formBags && amenity && (
          <FooterEditEntity
            entity={amenityUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBags, amenity)
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const AmenityTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'amenityUpdate'),
  {
    ...pick(actionCreators, 'gettyGetAmenityRequest', 'gettyUpdateAmenityRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(AmenityTranslationsView)
